import { isDefined, zipper } from "../utils";
import tma from "./tma";
import { RVI as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { period } = options;

    // Step 1: Calculate Change (Close - Open) and Range (High - Low)
    const changeData = data.map((d) => {
      if (
        !isDefined(d) ||
        !isDefined(d.close) ||
        !isDefined(d.open) ||
        !isDefined(d.high) ||
        !isDefined(d.low)
      ) {
        return undefined;
      }
      return {
        ...d,
        change: d.close - d.open,
        range: d.high - d.low,
      };
    });

    // Step 2: Apply 4-period Triangular Moving Average to Change and Range
    const changeTMA = tma().options({ windowSize: 4, sourcePath: "change" });

    const rangeTMA = tma().options({ windowSize: 4, sourcePath: "range" });

    const changeSmoothed = changeTMA(changeData);
    const rangeSmoothed = rangeTMA(changeData);

    // Step 3: Calculate rolling sums over the main period
    const rviCalculator = zipper().combine((changeSmooth, rangeSmooth) => {
      if (!isDefined(changeSmooth) || !isDefined(rangeSmooth)) {
        return undefined;
      }
      return {
        numerator: changeSmooth,
        denominator: rangeSmooth,
      };
    });

    const smoothedData = rviCalculator(changeSmoothed, rangeSmoothed);

    // Step 4: Calculate RVI using rolling sums
    const rviValues = [];
    const numeratorWindow = [];
    const denominatorWindow = [];

    for (let i = 0; i < smoothedData.length; i++) {
      const current = smoothedData[i];

      if (!isDefined(current)) {
        rviValues.push(undefined);
        continue;
      }

      // Add to rolling windows
      numeratorWindow.push(current.numerator);
      denominatorWindow.push(current.denominator);

      // Maintain window size
      if (numeratorWindow.length > period) {
        numeratorWindow.shift();
        denominatorWindow.shift();
      }

      // Calculate RVI when we have enough data
      if (numeratorWindow.length === period) {
        const sumNumerator = numeratorWindow.reduce((sum, val) => sum + val, 0);
        const sumDenominator = denominatorWindow.reduce(
          (sum, val) => sum + val,
          0,
        );

        // Prevent division by zero (as per ChartIQ implementation)
        const safeDenominator =
          sumDenominator === 0 ? 0.00000001 : sumDenominator;
        const rviValue = sumNumerator / safeDenominator;
        rviValues.push(rviValue);
      } else {
        rviValues.push(undefined);
      }
    }

    // Step 5: Apply 4-period Triangular Moving Average to RVI for signal line (ChartIQ approach)
    // Convert RVI values to data format for TMA
    const rviDataForSignal = rviValues.map((rvi) =>
      isDefined(rvi) ? { value: rvi } : undefined,
    );

    const signalTMA = tma().options({ windowSize: 4, sourcePath: "value" });

    const signalData = signalTMA(rviDataForSignal);

    // Extract signal values - TMA returns values directly, not in a tma property
    const signalValues = signalData;

    // Step 6: Compute histogram values
    const histogramValues = rviValues.map((rvi, i) => {
      const signal = signalValues[i];
      return isDefined(rvi) && isDefined(signal) ? rvi - signal : undefined;
    });

    // Step 7: Combine RVI, Signal, Histogram and direction (mirroring MACD divergenceDirection)
    const result = rviValues.map((rvi, i) => {
      const signal = signalValues[i];
      const histogram = histogramValues[i];
      const prevHistogram = i > 0 ? histogramValues[i - 1] : undefined;

      let histogramDirection = "up";
      if (isDefined(histogram) && isDefined(prevHistogram)) {
        histogramDirection = histogram >= prevHistogram ? "up" : "down";
      }

      return {
        rvi,
        signal,
        histogram,
        histogramDirection,
      };
    });

    return result;
  }

  calculator.undefinedLength = function () {
    const { period } = options;
    // period for RVI calculation + 4 for TMA signal - 1 = period + 3
    return period + 3;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
